import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { Modal, Button } from 'flowbite-react';
import router from 'next/router';
import { nanoid } from 'nanoid';
import toast from 'react-hot-toast';
import { Formik, FormikProps } from 'formik';
import { useMutation } from '@apollo/client';

import { withAuth } from '@/components/with_auth';
import Page from '@/components/layout/page';
import Title from '@/components/commons/page-elements/Title';
import styles from '@/components/accounts/Accounts.module.css';
import { initializedAtom, showBioOnboarding, userAtom } from '@/components/with_auth/State';
import routes from '@/services/routes';
import useIsEdit from '@/hooks/use_is_edit';
import { CREATE_BRIEF, DELETE_BRIEF } from '@/components/onboarding/Actions';
import GlobeOutline from '@/components/commons/icons/GlobeOutline';
import TrashBin from '@/components/commons/icons/TrashBin';
import Plus from '@/components/commons/icons/Plus';
import PencilSquareOutline from '@/components/commons/icons/PencilSquareOutline';
import ShareAll from '@/components/commons/icons/ShareAll';
import ProfileImage from '@/components/commons/ProfileImage';
import WarningCard from '@/components/commons/warning_card';
import { haveErrors, reportGeneralError } from '@/components/onboarding/Shared';
import { UPDATE_USER } from '@/components/settings/Actions';
import { handleValidationErrors, onCompletedHandler, onErrorHandler } from '@/services/apollo';
import ThumbsUpOutline from '@/components/commons/icons/ThumbsUpOutline';
import ButtonComponent, { ButtonSize, ButtonType } from '@/components/commons/button';
import { GENERIC_ERROR_TEXT, tryToSetBriefKey } from '@/utils/index';

import BioModal from '@/components/bio/BioModal';
import BioBriefModal from '@/components/bio/BioBriefModal';
import customModalTheme from '@/components/bio/UploadFileModalTheme';

function Bio() {
    const [user, setUser] = useRecoilState(userAtom);
    // TODO(tom) get selected brief into recoil state
    // const [selectedBrief, setSelectedBrief] = useRecoilState(selectedBriefAtom);

    const [modalState, setModalState] = useState({
        show: false, action: null, step: 0, isEdit: false,
    });
    const [confirmCloseModalState, setConfirmCloseModalState] = useState(false);
    const [briefToDelete, setBriefToDelete] = useState(null);
    const [briefOpen, setBriefOpen] = useState({ open: false, briefIndex: null });
    const [briefPanelOpen, setBriefPanelOpen] = useState(true);
    const [formError, setFormError] = useState('');
    const bioStatusformRef = useRef<FormikProps<any>>(null);
    const [updateUser] = useMutation(UPDATE_USER);
    const isBioOnboarding = useRecoilValue(showBioOnboarding);

    const setters = {
        setUser: useSetRecoilState(userAtom),
        setInitialized: useSetRecoilState(initializedAtom),
    };

    const [createBrief] = useMutation(CREATE_BRIEF);
    const [deleteBrief] = useMutation(DELETE_BRIEF);

    const { url, step, briefIndex } = useIsEdit();

    // const {
    //     refetch,
    // } = useQuery(FETCH_USER, {
    //     notifyOnNetworkStatusChange: true,
    //     fetchPolicy: 'no-cache',
    //     onCompleted: onCompletedHandler(() => {}, (data) => {
    //         setUser(data.user);
    //     }),
    // });

    // useEffect(() => {
    //     refetch();
    // // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [briefOpen]);

    const BRIEF_STEP = 12;

    useEffect(() => {
        if (briefIndex) {
            // set the brief we should be editing
            setBriefOpen({ open: false, briefIndex });
        }
        if (url) {
            setModalState({
                show: true, action: null, step, isEdit: false,
            });
        }
    }, [url, step, briefIndex]);

    const handleModalOpen = (openStep?: number, isEdit?: boolean) => {
        const newStep = typeof openStep !== 'number' ? 0 : openStep;
        const edit = typeof isEdit !== 'boolean' ? false : isEdit;
        setModalState({
            show: true, action: null, step: newStep, isEdit: edit,
        });
    };

    const handleCreateBrief = async () => {
        try {
            const initialBriefCount = user.briefs?.length || 0;
            const key: string = tryToSetBriefKey(user);

            const response = await createBrief({
                variables: {
                    input: {
                        attributes: {
                            name: key,
                            displayKey: key,
                        },
                    },
                },
            });
            const newBrief = response.data?.createBrief?.brief;
            if (newBrief) {
                // save the new brief onto the user
                setUser({ ...user, briefs: [...user.briefs, newBrief] });
                // Slightly weird, but opens the modal at the given step
                // we want to open at 1 (skip personalize) if this is our
                // second brief
                handleModalOpen(initialBriefCount ? 1 : 0);
                setBriefOpen({ open: false, briefIndex: newBrief.id });
            } else {
                // if we don't have a brief we failed to create!
                // retry with a new key
                const retryKey = nanoid();
                const retryRes = await createBrief({
                    variables: {
                        input: {
                            attributes: {
                                name: retryKey,
                                displayKey: retryKey,
                            },
                        },
                    },
                });
                const retryBrief = retryRes.data?.createBrief?.brief;
                if (retryBrief) {
                    // save the new brief onto the user
                    setUser({ ...user, briefs: [...user.briefs, retryBrief] });
                    handleModalOpen(initialBriefCount ? 1 : 0);
                    setBriefOpen({ open: false, briefIndex: retryBrief.id });
                    if (formError !== '') setFormError('');
                } else {
                    // we failed again on random key try, exceedingly rare
                    // but if this happens it's a dev issue, user can't action
                    setFormError(GENERIC_ERROR_TEXT);
                }
            }
        } catch (err) {
            reportGeneralError(err);
        }
    };

    const handleDeleteBrief = async (idx) => {
        try {
            const response = await deleteBrief({
                variables: {
                    input: {
                        id: idx,
                    },
                },
            });

            // remove the brief off the recoil user (global state)
            const deletedId = response.data?.deleteBrief?.brief?.id;
            if (deletedId) {
                const updatedUser = {
                    ...user,
                    briefs: user.briefs.filter((item) => item.id !== deletedId),
                };
                setUser(updatedUser);
            }
        } catch (err) {
            reportGeneralError(err);
        }
        setBriefToDelete(null);
    };

    const handleCopy = (displayKey) => {
        const briefLink = routes.BRIEF_URL(displayKey);
        navigator.clipboard.writeText(briefLink);
        toast.success('Brief link copied');
    };

    // TODO(tom) double check/rework logic when current brief logic is in recoil
    const handleModalClose = () => {
        setConfirmCloseModalState(false);
        if (modalState.step !== BRIEF_STEP) {
            setModalState({
                show: false, action: null, step: BRIEF_STEP, isEdit: false,
            });
        } else {
            setBriefOpen({ open: false, briefIndex: null });
            setModalState({
                show: false, action: null, step: 12, isEdit: false,
            });
        }
        // refetch();
        // if closing modal after coming from brief, go back
        // if (url) {
        //     router.push(url);
        // }
    };

    const handleConfirmBriefDeletion = (brief) => {
        setBriefToDelete(brief);
    };

    return (
        <Page>
            <div className={`flex flex-wrap fullHeightLessNavPage bg-lightGray bg-opacity-30 items-start ${styles.home}`}>
                <div className="w-full p-4 pt-8 sm:p-8">
                    <div className="flex justify-between relative flex-col md:flex-row">
                        <div className="text-left mb-5 w-full">
                            <Title breakpointFull="sm" title="Brief" subtitle="Get more out of your meetings with an official Sixty brief. Create different briefs for different segments of your network." />
                        </div>
                        {/* <button
                            type="button"
                            data-tooltip-id="tip"
                            data-tooltip-content="Cancel"
                            title="View Brief"
                            className="text-secondary"
                            onClick={() => setBriefOpen({ open: true, briefIndex: 0 })}
                        >
                            View Brief
                        </button> */}

                    </div>
                    {/* <div className={`${dashboardStyles.dashGraph} min-h-[450px] mb-8`}>
                        <h2 className="p-2 font-bold text-lg">Viewers</h2>
                        <MessagesSentLast7
                            bgColor={noonBlue}
                            legendConfig={legendConfig}
                            titleFont={titleFont}
                        />
                    </div> */}
                    {/*  */}

                    {isBioOnboarding && (
                        <div className="flex items-center justify-end mb-4">
                            <Formik
                                innerRef={bioStatusformRef}
                                onSubmit={(_frm, { setStatus, setSubmitting }) => {
                                    const attributes = {
                                        hasCreatedBio: true,
                                    };

                                    updateUser({
                                        variables: {
                                            updateUserInput: {
                                                attributes,
                                            },
                                        },
                                        notifyOnNetworkStatusChange: true,
                                        fetchPolicy: 'no-cache',
                                        onError: onErrorHandler((error) => {
                                            setSubmitting(false);
                                            reportGeneralError(error);
                                        }),
                                        onCompleted: onCompletedHandler(
                                            () => { },
                                            (data) => {
                                                setSubmitting(false);
                                                const { errors } = data.updateUser;
                                                if (haveErrors(errors)) {
                                                    handleValidationErrors(errors, setStatus, 'user');
                                                    return;
                                                }

                                                setters.setUser(
                                                    data.updateUser.user,
                                                );
                                                setters.setInitialized(true);
                                                setStatus({ errors: {} });

                                                router.push(routes.MY_SIXTY);
                                            },
                                        ),
                                    });
                                }}
                                initialValues={{
                                    hasCreatedAgendas: user?.hasCreatedAgendas,
                                }}
                            >
                                <ButtonComponent
                                    size={ButtonSize.base}
                                    icon={<ThumbsUpOutline className="w-3 h-3 m-auto mr-1" />}
                                    text="I'm done"
                                    type={ButtonType.submit}
                                    onClick={() => bioStatusformRef?.current?.handleSubmit()}
                                />
                            </Formik>
                        </div>
                    )}

                    <div className="flex w-full h-[35px] border-b">
                        <button
                            type="button"
                            className={`${briefPanelOpen ? 'text-progressBarBlue border-b border-progressBarBlue' : 'text-cleanSlate'} font-medium pb-2 mx-2 text-sm w-16`}
                            onClick={() => { setBriefPanelOpen(true); }}
                        >
                            My briefs
                        </button>
                        {/* <button
                            type="button"
                            className={`${!briefPanelOpen ?
                                 'text-progressBarBlue border-b border-progressBarBlue':
                                 'text-cleanSlate'} font-medium pb-2 text-sm w-16`}
                            onClick={() => { setBriefPanelOpen(false); }}
                        >
                            Files
                        </button> */}

                    </div>

                    {
                        formError !== '' && (
                            <div className="w-full my-[20px]">
                                <WarningCard message={formError} />
                            </div>
                        )
                    }

                    {briefPanelOpen
                        && (
                            <div>
                                <div className="flex justify-between items-center bg-slate-200 my-4 px-4 rounded-lg h-[58px]">
                                    <div className="flex">
                                        <p>
                                            {user?.briefs.length || '0'}
                                            {' '}
                                            briefs
                                        </p>
                                    </div>
                                    <div className="flex">
                                        <button
                                            type="button"
                                            data-tooltip-id="tip"
                                            data-tooltip-content="New brief"
                                            title="New brief"
                                            disabled={user?.briefs.length > 0}
                                            onClick={() => handleCreateBrief()}
                                            className="flex rounded-md text-sm border-black border text-black h-[35px] px-4 bg-white items-center"
                                        >
                                            <Plus className="h-3 w-3 mr-3" />
                                            New brief
                                        </button>
                                    </div>

                                </div>
                                <div className="flex flex-col">
                                    {user && user.briefs && user.briefs.map((briefObj) => {
                                        const titleAndCompanyPieces = [
                                            briefObj?.title,
                                            briefObj?.company,
                                        ];
                                        const titleAndCompany = titleAndCompanyPieces.filter((piece) => piece).join(', ');

                                        return (
                                            <div key={briefObj.id} className="flex flex-col sm:flex-row items-center bg-white h-auto rounded-lg justify-between my-2 pb-6 sm:pb-0">

                                                <div className="flex items-center w-full sm:w-1/3">
                                                    <div className="p-2">
                                                        <ProfileImage brief={briefObj} height="h-[60px]" width="w-[60px]" />
                                                    </div>
                                                    <div className="flex flex-col break-all">
                                                        <div className="flex">
                                                            <p className="text-sm">
                                                                {titleAndCompany}
                                                            </p>

                                                        </div>
                                                    </div>

                                                </div>
                                                <div className="flex items-center justify-start w-full sm:w-1/3 pl-4 sm:pl-0 pt-0 break-all">
                                                    <GlobeOutline className="w-5 h-5 mr-3" />
                                                    <p className="p-0 ">{`sixty.co/${briefObj.displayKey}`}</p>
                                                </div>
                                                <div className="flex self-start sm:self-center items-center sm:justify-end h-[16px] hover:cursor-pointer pl-4 pt-6 sm:pt-0 sm:pl-0 ">
                                                    <button aria-label="Share" type="button" onClick={() => { handleCopy(briefObj.displayKey); }}>
                                                        <ShareAll className="w-5 h-5 mr-3 text-progressBarBlue" />
                                                    </button>
                                                    <button aria-label="Edit" type="button" onClick={() => setBriefOpen({ open: true, briefIndex: briefObj.id })}>
                                                        <PencilSquareOutline className="w-5 h-5 mr-3 text-progressBarBlue" />
                                                    </button>
                                                    <button aria-label="Delete" type="button" onClick={() => handleConfirmBriefDeletion(briefObj)}>
                                                        <TrashBin className="w-5 h-5 mr-3 text-progressBarBlue" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                </div>
            </div>
            {modalState.step !== BRIEF_STEP
                && (
                    <BioModal
                        step={modalState.step}
                        display={modalState.show}
                        openBrief={briefOpen}
                        isEdit={modalState.isEdit}
                        openModal={handleModalOpen}
                        // TODO(tom) will we ever not have a user?
                        closeModal={() => (!user
                            ? setConfirmCloseModalState(true)
                            : handleModalClose())}
                    />
                )}
            {/* TODO(tom) rework so this is one modal */}
            {briefOpen.open && user.briefs && (
                <BioBriefModal
                    display={briefOpen}
                    handleModal={handleModalOpen}
                    closeModal={() => (!user
                        ? setConfirmCloseModalState(true)
                        : handleModalClose())}
                />
            )}
            <Modal dismissible className="p-0 m-0 font-instrument absolute z-[1000]" show={confirmCloseModalState} onClose={() => setConfirmCloseModalState(false)} position="center" size="xl">
                <div className="flex flex-col h-[250px] items-center justify-center">
                    <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400 w-2/3">
                        Are you sure you want to leave without completing your brief?
                    </h3>
                    <div className="flex justify-center gap-4">
                        <Button type="button" onClick={() => handleModalClose()} label="Yes" />
                        <Button type="button" onClick={() => setConfirmCloseModalState(false)} label="No" />
                    </div>
                </div>
            </Modal>

            <Modal dismissible className="absolute z-[1000]" theme={customModalTheme} show={briefToDelete} onClose={() => setBriefToDelete(null)} position="center" size="sm">
                <div className="flex flex-col">
                    <h3 className="font-instrument font-bold pb-4">
                        Are you sure you want to delete your brief?
                    </h3>
                    <div className="flex flex-col sm:flex-row justify-end gap-4">
                        <Button
                            color="dark"
                            onClick={() => handleDeleteBrief(briefToDelete.id)}
                            className="bg-bloodRed"
                        >
                            Yes
                        </Button>
                        <Button
                            color="light"
                            onClick={() => setBriefToDelete(null)}
                        >
                            No
                        </Button>
                    </div>
                </div>
            </Modal>
        </Page>
    );
}

export default withAuth(Bio);
